import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportGitHubIssueLink } from '../../../models/Support/ISupportGitHub';
import {
	useSyncGitHubIssueMutation,
	useUnlinkGitHubIssueLinkMutation,
} from '../../../queries/Support/useSupportGitHub';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { ConfirmModal } from '../../components/supportShared/ConfirmModal';
import { findSourceIssue, GitHubPanelTone } from './githubPanelPresentation';
import { styles } from './styles';
import { formatShortDateTime } from './supportTicketPresentation';

export const PILL_TONE_STYLES: Record<GitHubPanelTone, (typeof styles)[keyof typeof styles]> = {
	neutral: styles.githubPillNeutral,
	progress: styles.githubPillProgress,
	positive: styles.githubPillPositive,
	caution: styles.githubPillCaution,
	critical: styles.githubPillCritical,
	engineering: styles.githubPillEngineering,
};

const getRepoLabel = (issue: ISupportGitHubIssueLink) => issue.repository || `${issue.owner}/${issue.repo}`;

/** Title plus the issue reference — the one row every record card leads with. */
export const GitHubRecordHeader: React.FC<{ issue: ISupportGitHubIssueLink }> = ({ issue }) => (
	<div className={css(styles.githubRecordTitleRow)}>
		<a href={issue.url} target='_blank' rel='noopener noreferrer' className={css(styles.githubRecordTitle)}>
			{issue.title || 'Untitled issue'}
		</a>
		<a
			href={issue.url}
			target='_blank'
			rel='noopener noreferrer'
			className={css(styles.githubRecordRef)}
			title={`${getRepoLabel(issue)}#${issue.number}`}
		>
			#{issue.number} ↗
		</a>
	</div>
);

/**
 * GitHub state, priority and labels. Subordinate to the lifecycle on a managed record, but the only
 * status a manually linked issue has.
 */
export const GitHubIssueMetaRow: React.FC<{ issue: ISupportGitHubIssueLink }> = ({ issue }) => (
	<div className={css(styles.githubIssueLabels)}>
		<span
			className={css(
				styles.githubIssueState,
				issue.state === 'Closed' ? styles.githubIssueStateClosed : styles.githubIssueStateOpen
			)}
		>
			{issue.state}
		</span>
		<span className={css(styles.githubIssuePriority)}>{issue.priority}</span>
		{issue.labels?.map(label => (
			<span key={label} className={css(styles.githubIssueLabel)}>
				{label}
			</span>
		))}
		{issue.targetCodeRepos?.map(repo => (
			<span key={repo} className={css(styles.githubIssueTargetLabel)}>
				{repo}
			</span>
		))}
	</div>
);

/** The engineering issue an escalation workflow produced, so the link itself can't be broken. */
export const GitHubEngineeringCard: React.FC<{
	ticketId: string;
	issue: ISupportGitHubIssueLink;
	issues: ISupportGitHubIssueLink[];
	enabled: boolean;
}> = ({ ticketId, issue, issues, enabled }) => {
	const syncMutation = useSyncGitHubIssueMutation(ticketId, issue.id);
	const sourceIssue = findSourceIssue(issue, issues);

	return (
		<div className={css(styles.githubRecordCard, styles.githubRecordCardEngineering)}>
			<GitHubRecordHeader issue={issue} />
			<GitHubIssueMetaRow issue={issue} />
			<span className={css(styles.githubStageText)}>
				Filed {formatShortDateTime(issue.createdAt)}
				{sourceIssue ? ` from investigation #${sourceIssue.number}` : ''}
			</span>

			<div className={css(styles.githubCardFooter)}>
				<Button
					variant='outline'
					size='sm'
					onClick={() => syncMutation.mutate()}
					disabled={!enabled || syncMutation.isPending}
				>
					{syncMutation.isPending ? 'Refreshing...' : 'Refresh'}
				</Button>
				<span className={css(styles.githubCardFooterNote)}>Managed · can't unlink</span>
			</div>

			{syncMutation.isError && (
				<div className={css(styles.githubPanelError)} role='alert' aria-live='polite'>
					{getApiErrorMessage(syncMutation.error, 'Failed to refresh GitHub issue.')}
				</div>
			)}
		</div>
	);
};

/** A manually created or linked issue: no investigation, so the agent keeps full control of the link. */
export const GitHubLinkedIssueCard: React.FC<{
	ticketId: string;
	issue: ISupportGitHubIssueLink;
	enabled: boolean;
}> = ({ ticketId, issue, enabled }) => {
	const [showUnlinkConfirm, setShowUnlinkConfirm] = React.useState(false);
	const syncMutation = useSyncGitHubIssueMutation(ticketId, issue.id);
	const unlinkMutation = useUnlinkGitHubIssueLinkMutation(ticketId, issue.id);

	return (
		<div className={css(styles.githubRecordCard)}>
			<GitHubRecordHeader issue={issue} />
			<GitHubIssueMetaRow issue={issue} />

			<div className={css(styles.githubCardFooter)}>
				<Button
					variant='outline'
					size='sm'
					onClick={() => syncMutation.mutate()}
					disabled={!enabled || syncMutation.isPending}
				>
					{syncMutation.isPending ? 'Refreshing...' : 'Refresh'}
				</Button>
				<Button
					variant='outline'
					size='sm'
					onClick={() => setShowUnlinkConfirm(true)}
					disabled={!enabled || unlinkMutation.isPending}
				>
					{unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
				</Button>
			</div>

			{syncMutation.isError && (
				<div className={css(styles.githubPanelError)} role='alert' aria-live='polite'>
					{getApiErrorMessage(syncMutation.error, 'Failed to refresh GitHub issue.')}
				</div>
			)}
			{unlinkMutation.isError && (
				<div className={css(styles.githubPanelError)} role='alert' aria-live='polite'>
					{getApiErrorMessage(unlinkMutation.error, 'Failed to unlink GitHub issue.')}
				</div>
			)}
			{showUnlinkConfirm && (
				<ConfirmModal
					title='Unlink GitHub Issue'
					message={`Unlink ${getRepoLabel(issue)}#${issue.number} from this ticket?`}
					confirmLabel='Unlink'
					onConfirm={() => {
						setShowUnlinkConfirm(false);
						unlinkMutation.mutate();
					}}
					onCancel={() => setShowUnlinkConfirm(false)}
				/>
			)}
		</div>
	);
};
